import { isNull, isUndefined } from 'lodash/fp';
import React, { useCallback, useMemo } from 'react';
import { Options } from 'react-select';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { IconButton } from '../../../shared/button/IconButton';
import { Dropdown, DropdownOption } from '../../../shared/dropdown/Dropdown';
import { Search } from '../../../shared/icons';
import styles from './Theoretical.module.scss';
import { NettingEngineSurvey, NettingEngineSubQuestion, updateNettingEngineData, NettingEngineCounterparty, setLeiModalOpen, setIsChangingQuestion, NettingEngineAgreement, getCurrentNettingEngineData, ISDANettingEngineData } from '../store';
import { NettingEngineEntity } from '../../../admin/entity/store';
import { fetchOpinionSubCounterpartyTypesStarted, resetOpinionSubCounterpartyTypes, getIsFetchingSubCounterpartyTypes, getAvailableSubCounterpartyTypes } from '../../../admin/sub-counterparty-types/store';
import { getUserHasFeaturePermissionNoAdmin } from '../../../auth/login/store';
import { FeaturePermission } from '../../../admin/users/store';
import { CreatableDropdown } from '../../../shared/dropdown/CreateableDropdown';

const { green, red } = styles;

interface TheoreticalDropdownQuestionsProps {
    surveyQuestionIndex: number;
    nettingEngineSurvey: NettingEngineSurvey[];
    counterpartySubQuestion: NettingEngineSubQuestion;
    subCounterpartyQuestion: NettingEngineSubQuestion;
    allEntities: NettingEngineEntity[];
    counterpartyNameKnown: boolean | null;
    jurisdictionOptions: { value: string, label: string }[];
    updateCounterpartyOfficeOptions: (value: DropdownOption[]) => void;
    transitionToQuestionByIncrement: (increment?: number) => void
    transitionToNextQuestion: () => void;
    paymentMethodQuestion: NettingEngineSurvey | null;
}

export const TheoreticalDropdownQuestions: React.FC<TheoreticalDropdownQuestionsProps> = ({
    surveyQuestionIndex,
    nettingEngineSurvey,
    counterpartySubQuestion,
    subCounterpartyQuestion,
    allEntities,
    counterpartyNameKnown,
    jurisdictionOptions,
    updateCounterpartyOfficeOptions,
    transitionToQuestionByIncrement,
    transitionToNextQuestion,
    paymentMethodQuestion
}) => {
    const { counterparty } = useAppSelector(getCurrentNettingEngineData);
    const dispatch = useAppDispatch();
    const isFetchingSubCounterparties = useAppSelector(getIsFetchingSubCounterpartyTypes);
    const subCounterpartyList = useAppSelector(getAvailableSubCounterpartyTypes);

    const updatedSubCounterpartyDropdownOptions = useMemo(() => {
        const includedIdsExist = subCounterpartyList.some(({ isIncludedInOpinion }) => !isUndefined(isIncludedInOpinion));
        if (!includedIdsExist) {
            return subCounterpartyQuestion.dropdownOptions!;
        }

        return subCounterpartyQuestion.dropdownOptions?.map(({ value, label }) => {
            const subCounterparty = subCounterpartyList.find(({ subCounterpartyName }) => subCounterpartyName === label);
            if (!subCounterparty || isUndefined(subCounterparty.isIncludedInOpinion)) {
                return { value, label };
            }

            const colour = subCounterparty.isIncludedInOpinion ? green : red;
            return { value, label, highlightColour: colour };
        });
    }, [subCounterpartyList, subCounterpartyQuestion]);

    const selectedCounterpartyJurisdiction = useCallback((entityId: string | null) => !isNull(entityId) && allEntities.find(entity => entity.entityId === parseInt(entityId!))?.jurisdiction || undefined, [allEntities]);
    const selectedCounterpartyType = useCallback((entityId: string | null) => {
        if (!isNull(entityId)) {
            const entityInQuestion = allEntities.find(entity => entity.entityId === parseInt(entityId!));
            if (!isUndefined(entityInQuestion)) {
                if (!isNull(entityInQuestion.opinionCounterpartyType)) {
                    const { aiConfirmed, value } = entityInQuestion.opinionCounterpartyType;
                    if (!isNull(value)) {
                        return { value, aiConfirmed, subCounterpartyId: entityInQuestion.subCounterpartyTypeId };
                    }
                }
            }
        }
        return undefined;
    }, [allEntities]);

    const updateCounterpartyProperties = useCallback((value: string, key: keyof ISDANettingEngineData, counterpartyKey: keyof NettingEngineCounterparty) => {
        if (counterpartyKey === 'entityId') {
            dispatch(updateNettingEngineData(key, value, counterpartyKey));
            const entityOpinionJurisdiction = selectedCounterpartyJurisdiction(value);
            if (!isUndefined(entityOpinionJurisdiction)) {
                const matchedJurisdiction = jurisdictionOptions.map(({ value }) => value).find(eachJurisdiction => eachJurisdiction === entityOpinionJurisdiction);
                if (!isUndefined(matchedJurisdiction)) {
                    dispatch(setIsChangingQuestion(true));
                    dispatch(updateNettingEngineData(key, matchedJurisdiction, 'jurisdiction'));
                    const counterpartyType = selectedCounterpartyType(value);
                    if (!isUndefined(counterpartyType) && counterpartyType.value.length > 0) {
                        dispatch(fetchOpinionSubCounterpartyTypesStarted(entityOpinionJurisdiction, counterpartyType.value));
                        dispatch(updateNettingEngineData(key, counterpartyType.value, 'counterpartyClassification'));
                        const subCounterpartyId = counterpartyType.subCounterpartyId;
                        if (!isNull(subCounterpartyId)) {
                            dispatch(updateNettingEngineData(key, subCounterpartyId, 'subCounterpartyTypeId'));
                        }
                        if (counterpartyType.aiConfirmed && subCounterpartyId) {
                            transitionToQuestionByIncrement(2);
                        } else {
                            transitionToNextQuestion();
                        }
                    } else {
                        transitionToNextQuestion();
                    }
                }
            }
        }
        if (['jurisdiction', 'counterpartyClassification'].includes(counterpartyKey)) {
            dispatch(updateNettingEngineData(key, null, 'subCounterpartyTypeId'));
            dispatch(resetOpinionSubCounterpartyTypes());
            if (counterpartyKey === 'jurisdiction') {
                // if its a new jurisdiction we can fetch the list if the counterparty is defined
                dispatch(updateNettingEngineData(key, value, 'jurisdiction'));
                if (!isNull(counterparty.counterpartyClassification)) {
                    dispatch(fetchOpinionSubCounterpartyTypesStarted(value, counterparty.counterpartyClassification));
                }
                transitionToNextQuestion();
            }
            if (counterpartyKey === 'counterpartyClassification' && !isNull(counterparty.jurisdiction)) {
                // if we're changing the counterparty type then check if we have a jurisdiction
                const establishIfIncluded = true;
                dispatch(updateNettingEngineData(key, value, 'counterpartyClassification'));
                dispatch(fetchOpinionSubCounterpartyTypesStarted(counterparty.jurisdiction, value, establishIfIncluded));
            }
        }
        if (counterpartyKey === 'subCounterpartyTypeId') {
            dispatch(setIsChangingQuestion(true));
            dispatch(updateNettingEngineData(key, parseInt(value), counterpartyKey));
            transitionToNextQuestion();
        }
    }, [dispatch, jurisdictionOptions, selectedCounterpartyJurisdiction, selectedCounterpartyType, counterparty, transitionToNextQuestion, transitionToQuestionByIncrement]);

    const updateDropdownAnswer = useCallback((option: DropdownOption | Options<DropdownOption> | null, key: keyof ISDANettingEngineData, counterpartyKey?: keyof NettingEngineCounterparty, isMulti?: boolean) => {
        if (isMulti) {
            let selected: string[] = [];
            if (!isNull(option) && (option as Options<DropdownOption>).length > 0) {
                selected = (option as Options<DropdownOption>).map(({ value }) => value);
            }
            dispatch(updateNettingEngineData(key, selected));
        } else {
            if (!isNull(option)) {
                const value = (option as DropdownOption).value;
                if (!isUndefined(counterpartyKey)) {
                    updateCounterpartyProperties(value, key, counterpartyKey);
                } else {
                    dispatch(updateNettingEngineData(key, value, counterpartyKey));
                    if (key !== 'agreement' || key === 'agreement' && value !== NettingEngineAgreement.ISDA_1992_MASTER) {
                        dispatch(setIsChangingQuestion(true));
                        transitionToNextQuestion();
                    }
                }
            } else {
                if (!isUndefined(counterpartyKey) && ['entityId', 'jurisdiction', 'counterpartyClassification'].includes(counterpartyKey)) {
                    dispatch(updateNettingEngineData(key, null, 'subCounterpartyTypeId'));
                    dispatch(resetOpinionSubCounterpartyTypes());
                }
                dispatch(updateNettingEngineData(key, null, counterpartyKey));
            }
            if (surveyQuestionIndex + 1 === nettingEngineSurvey.length) {
                setTimeout(() => {
                    dispatch(setIsChangingQuestion(false));
                }, 750);
            }
        }
    }, [dispatch, nettingEngineSurvey, surveyQuestionIndex, transitionToNextQuestion, updateCounterpartyProperties]);

    const hasEntityManagementPermission = useAppSelector(getUserHasFeaturePermissionNoAdmin([FeaturePermission.ENTITY_MANAGEMENT]));
    const showLeiSearch = useMemo(() => hasEntityManagementPermission && surveyQuestionIndex === 1 && !!counterpartyNameKnown, [hasEntityManagementPermission, surveyQuestionIndex, counterpartyNameKnown]);

    const openLeiSearch = useCallback(() => dispatch(setLeiModalOpen(true)), [dispatch]);

    const { key, counterpartyKey, value, dropdownOptions, isMulti, closeOnSelect } = nettingEngineSurvey[surveyQuestionIndex];
    const showSubCounterpartyQuestion = useMemo(() => surveyQuestionIndex === 2 && !isNull(counterparty.jurisdiction) && !isNull(counterparty.counterpartyClassification), [surveyQuestionIndex, counterparty]);

    const unknownSubCounterparty = 'Note: If you do not know the sub counterparty type you can leave this blank, we will work with the top level counterparty type for netting considerations and analysis.';
    const noSubCounterpartyOptions = 'Note: Please note there is no sub counterparty information configured for the selected jurisdiction and counterparty pair so this can be left blank.';
    const unknownPaymentMethod = 'Note: If the payment method is unknown you can leave this blank, we will assume the second method applies for netting considerations and analysis.';

    const subCounterpartyNote = useMemo(() => {
        if (counterparty.jurisdiction && counterparty.counterpartyClassification && !subCounterpartyQuestion.dropdownOptions.length && !isFetchingSubCounterparties) {
            return noSubCounterpartyOptions;
        }
        if (!isFetchingSubCounterparties) {
            return unknownSubCounterparty;
        }
        return '';
    }, [subCounterpartyQuestion, noSubCounterpartyOptions, unknownSubCounterparty, counterparty, isFetchingSubCounterparties]);

    const surveyNote = useMemo(() => {
        let note = '';
        if (showSubCounterpartyQuestion) {
            note = subCounterpartyNote;
        }
        if (surveyQuestionIndex === 3 && paymentMethodQuestion && isNull(paymentMethodQuestion.value)) {
            note = unknownPaymentMethod;
        }
        return note;
    }, [showSubCounterpartyQuestion, subCounterpartyNote, surveyQuestionIndex, paymentMethodQuestion]);

    return (
        <>
            <div className={styles.surveyInput}>
                <div className={styles.surveyDropdownWrapper}>
                    {key === 'counterpartyOffices' ? <CreatableDropdown
                        value={(value as DropdownOption)}
                        options={dropdownOptions!}
                        onChange={value => updateDropdownAnswer(value, key, counterpartyKey, isMulti)}
                        updateOptions={updateCounterpartyOfficeOptions}
                        isMulti={isMulti}
                        closeOnSelect={closeOnSelect}
                    /> :
                        <Dropdown
                            value={(value as DropdownOption)}
                            options={dropdownOptions!}
                            onChange={value => updateDropdownAnswer(value, key, counterpartyKey, isMulti)}
                            isMulti={isMulti}
                            closeOnSelect={closeOnSelect}
                        />
                    }
                    {surveyQuestionIndex === 1 && !!counterpartyNameKnown &&
                        <div className={styles.subQuestionWrapper}>
                            <div className={styles.counterpartySubQuestion}>Counterparty Jurisdiction</div>
                            <Dropdown
                                value={(counterpartySubQuestion.value as DropdownOption)}
                                options={counterpartySubQuestion.dropdownOptions!}
                                onChange={val => updateDropdownAnswer(val, counterpartySubQuestion.key, counterpartySubQuestion.counterpartyKey)}
                            />
                        </div>
                    }
                    {showSubCounterpartyQuestion &&
                        <div className={styles.subQuestionWrapper}>
                            <div className={styles.counterpartySubQuestion}>Sub Counterparty Type</div>
                            <Dropdown
                                value={(subCounterpartyQuestion.value as DropdownOption)}
                                options={updatedSubCounterpartyDropdownOptions}
                                onChange={val => updateDropdownAnswer(val, subCounterpartyQuestion.key, subCounterpartyQuestion.counterpartyKey)}
                            />
                        </div>
                    }
                    {!isNull(paymentMethodQuestion) && surveyQuestionIndex === 3 &&
                        <div className={styles.subQuestionWrapper}>
                            <div className={styles.counterpartySubQuestion}>{paymentMethodQuestion.question}</div>
                            <Dropdown
                                value={(paymentMethodQuestion.value as DropdownOption)}
                                options={paymentMethodQuestion.dropdownOptions || []}
                                onChange={val => updateDropdownAnswer(val, paymentMethodQuestion.key)}
                            />
                        </div>
                    }
                </div>
                <div className={styles.inputWrapper}>
                    {showLeiSearch && <IconButton icon={Search} onClick={openLeiSearch} fontSize={35} />}
                </div>
            </div>
            <div className={styles.surveyMessage}>{surveyNote}</div>
        </>
    );
};
